import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouteContext } from '@tanstack/react-router';
import { Avatar, Button, Empty, Flex, message, Result, Tag, Tooltip, Typography } from 'antd';
import { isAxiosError } from 'axios';
import { ArrowLeft, Mail, Plus, RotateCw, Settings, Trash2 } from 'lucide-react';
import { useState } from 'react';

import {
  getListInvitationsQueryKey,
  useListInvitations,
  useListOrganizationMemberships,
  useResendInvitation,
  useRevokeInvitation,
} from '../../../../api';
import { usePermissions } from '../../../../modules/account/hooks';
import { EntityAvatar } from '../../../../modules/avatar/components/entity-avatar';
import {
  EditOrganizationMemberDrawer,
  InviteOrganizationMemberDrawer,
} from '../../../../modules/organization/components/organization-member-drawer';
import { SettingsLayoutContent } from '../../../../ui/components/layout';
import { List } from '../../../../ui/components/list';
import { Loader } from '../../../../ui/components/loader';
import { Record } from '../../../../ui/components/record';
import { formatDateTime } from '../../../../ui/utils/format-date';

const RevokeInvitationButton = ({
  organizationId,
  organizationMembershipInvitationId,
}: {
  organizationId: string;
  organizationMembershipInvitationId: string;
}) => {
  const queryClient = useQueryClient();

  const { mutate: revokeInvitation, isPending: isRevoking } = useRevokeInvitation({
    mutation: {
      onSuccess: () => {
        message.success('Invitation cancelled');
        void queryClient.invalidateQueries({
          queryKey: getListInvitationsQueryKey(organizationId),
        });
      },
      onError: () => {
        message.error('Failed to cancel invitation');
      },
    },
  });

  return (
    <Button
      data-testid="invitation-cancel"
      variant="text"
      color="danger"
      icon={<Trash2 size="1em" />}
      loading={isRevoking}
      onClick={() => {
        revokeInvitation({
          organizationId,
          organizationMembershipInvitationId,
        });
      }}
    />
  );
};

const ResendInvitationButton = ({
  organizationId,
  organizationMembershipInvitationId,
}: {
  organizationId: string;
  organizationMembershipInvitationId: string;
}) => {
  const queryClient = useQueryClient();

  const { mutate: resendInvitation, isPending: isResending } = useResendInvitation({
    mutation: {
      onSuccess: () => {
        message.success('Invitation resent successfully');

        queryClient.invalidateQueries({
          queryKey: getListInvitationsQueryKey(organizationId),
        });
      },
      onError: () => {
        message.error('Failed to resend invitation');
      },
    },
  });

  return (
    <Button
      data-testid="invitation-resend"
      variant="text"
      color="default"
      icon={<RotateCw size="1em" />}
      loading={isResending}
      onClick={() => {
        resendInvitation({
          organizationId,
          organizationMembershipInvitationId,
        });
      }}
    />
  );
};

const SettingsTeamRoute = () => {
  const { selectedOrganization, account } = useRouteContext({
    from: '/_authenticated/_app',
  });
  const { can } = usePermissions();
  const organizationId = selectedOrganization.id;
  const currentUserId = account.id;

  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [selectedMembershipId, setSelectedMembershipId] = useState<string | null>(null);

  const {
    data: membersResponse,
    error: membersError,
    status: membersStatus,
    refetch: refetchMembers,
  } = useListOrganizationMemberships(organizationId);
  const members = membersResponse?.data?.items;

  const { data: invitationsResponse } = useListInvitations(organizationId);
  const invitations = invitationsResponse?.data?.items;

  const canManageMembers = can('settings', 'manageMembershipsAndRoles');

  const content = {
    pending: <Loader />,
    error:
      isAxiosError(membersError) && membersError.response?.status === 403 ? (
        <Result
          status="403"
          title="Access Denied"
          subTitle="You do not have permission to view or edit this page. Please contact your administrator."
          extra={
            <Button icon={<ArrowLeft size="1em" />} onClick={() => window.history.back()}>
              Go back
            </Button>
          }
        />
      ) : (
        <Result
          status="error"
          title="Failed to load!"
          subTitle="Please try again. If this keeps happening, check your connection or contact support."
          extra={
            <Button
              icon={<RotateCw size="1em" />}
              onClick={() => {
                refetchMembers();
              }}
            >
              Try again
            </Button>
          }
        />
      ),
    success: (
      <Flex vertical gap={24}>
        <Flex vertical gap={8} data-testid="members-list">
          <Typography.Text>Team Members</Typography.Text>

          {!members?.length ? (
            <Empty description="No team members" />
          ) : (
            <List
              items={members}
              renderItem={(record) => {
                const fullName =
                  record.user?.firstName || record.user?.lastName
                    ? `${record.user.firstName ?? ''} ${record.user.lastName ?? ''}`.trim()
                    : 'Team Member';
                return {
                  key: record.id,
                  reactNode: (
                    <Record
                      left={
                        <EntityAvatar
                          kind="user"
                          id={record.user.id}
                          index={record.user.avatarIndex}
                          name={fullName}
                          size={50}
                        />
                      }
                      title={fullName}
                      description={record.user?.email || 'no-email@provided.com'}
                      right={
                        <Flex align="center" gap={4}>
                          <Tag>{record.role?.name || 'No Role'}</Tag>
                          {record.disabledAt ? <Tag color="orange">Disabled</Tag> : null}
                          {canManageMembers && (
                            <Tooltip title="Edit">
                              <Button
                                data-testid="member-edit"
                                variant="text"
                                color="default"
                                icon={<Settings size="1em" />}
                                onClick={() => setSelectedMembershipId(record.id)}
                              />
                            </Tooltip>
                          )}
                        </Flex>
                      }
                    />
                  ),
                };
              }}
            />
          )}
        </Flex>

        {invitations?.length ? (
          <Flex vertical gap={8} data-testid="invitations-list">
            <Typography.Text>Pending Invitations</Typography.Text>
            <List
              items={invitations}
              renderItem={(record) => ({
                key: record.id,
                reactNode: (
                  <Record
                    left={<Avatar size={32} icon={<Mail size={16} />} />}
                    title={record.email}
                    description={formatDateTime(record.sentAt, 'Not sent')}
                    right={
                      <Flex align="center" gap={4}>
                        <Tag>{record.role.name}</Tag>
                        {canManageMembers ? (
                          <>
                            <Tooltip title="Resend invitation">
                              <ResendInvitationButton
                                organizationId={organizationId}
                                organizationMembershipInvitationId={record.id}
                              />
                            </Tooltip>
                            <Tooltip title="Cancel invitation">
                              <RevokeInvitationButton
                                organizationId={organizationId}
                                organizationMembershipInvitationId={record.id}
                              />
                            </Tooltip>
                          </>
                        ) : null}
                      </Flex>
                    }
                  />
                ),
              })}
            />
          </Flex>
        ) : null}

        {selectedMembershipId && (
          <EditOrganizationMemberDrawer
            open
            organizationId={organizationId}
            organizationMembershipId={selectedMembershipId}
            currentUserId={currentUserId}
            onClose={() => {
              setSelectedMembershipId(null);
            }}
          />
        )}

        {isInviteOpen ? (
          <InviteOrganizationMemberDrawer
            open={isInviteOpen}
            organizationId={organizationId}
            onClose={() => {
              setIsInviteOpen(false);
            }}
          />
        ) : null}
      </Flex>
    ),
  };

  return (
    <SettingsLayoutContent
      title="Team"
      description="Manage team members and invitations."
      action={
        canManageMembers && (
          <Button
            data-testid="member-invite"
            color="default"
            variant="filled"
            size="large"
            icon={<Plus size={16} />}
            onClick={() => setIsInviteOpen(true)}
          />
        )
      }
    >
      {content[membersStatus]}
    </SettingsLayoutContent>
  );
};

export const Route = createFileRoute('/_authenticated/_app/settings/team')({
  component: SettingsTeamRoute,
});
